import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle, useRef } from "react";
import { Modal, Form, message, Spin, Alert, Space, Typography as AntdTypography } from "antd";
import { RetweetOutlined } from "@ant-design/icons";

import "./GeneralEmailEditor.css"; // 引入CSS
// 导入拆分后的组件
import EmailEditorHeader from "./components/EmailEditorHeader";
import EmailEditorForm from "./components/EmailEditorForm";
import EmailEditorContent from "./components/EmailEditorContent";
import EmailEditorFooter from "./components/EmailEditorFooter";
import GlobalDropZone from "./components/GlobalDropZone";
// 导入自定义hooks
import { useContentBlocks } from "./hooks/useContentBlocks";
import { useEmailEditor } from "./hooks/useEmailEditor";
// 导入常量
import { DEFAULT_TOOLBAR_BUTTONS } from "./utils/constants";
// 导入主题解析工具
import { parseSubjectTemplate, hasValidContentBlocks } from "./utils/subjectTemplateParser";
import { cleanHtmlForEmail, renderContentBlocksToHtml, extractInlineImages } from "./utils/htmlCleanup";
import { resetImageSizeTracker } from "./extensions/ImageUploadExtension";
import { uploadFile } from "../../api/attachmentApi";
import { getInitialOptionValues, getAllOptions, SendingOptionType } from "../../config/emailTypeConfig";
import { getEffectiveData, getDataRecordCount, getDataRecordIds } from "./utils/dataScopeResolver";

/**
 * 重发模式工具：把 "a@x.com, b@x.com; c@x.com" 这种带分号/逗号混用的字符串拆成去重后的邮箱数组
 * 用于"沿用原 TO/CC"按钮和二次确认收件人比对（FR-003 / S1.4 / Edge Case 多分隔符）
 */
const splitRecipientString = (raw) => {
    if (!raw || typeof raw !== "string") return [];
    return [
        ...new Set(
            raw
                .split(/[,;]/)
                .map((s) => s.trim())
                .filter(Boolean),
        ),
    ];
};

/**
 * 判断两个收件人数组（去大小写、去重后）是否完全相同
 * 用于 S1.4 "收件人与原邮件完全一致"二次确认
 */
const recipientsExactlyEqual = (currentList, originalList) => {
    if (!Array.isArray(currentList) || !Array.isArray(originalList)) return false;
    const norm = (arr) => new Set(arr.map((s) => String(s).toLowerCase().trim()).filter(Boolean));
    const currentSet = norm(currentList);
    const originalSet = norm(originalList);
    if (currentSet.size === 0 && originalSet.size === 0) return false; // 都空不算"完全一致"
    if (currentSet.size !== originalSet.size) return false;
    for (const v of currentSet) {
        if (!originalSet.has(v)) return false;
    }
    return true;
};

const getSnapshotRecordIds = (data) => {
    const ids = getDataRecordIds(data);
    if (ids.length > 0) return ids;
    return data?.id ? [data.id] : [];
};

const getSnapshotRecordCount = (data) => {
    const count = getDataRecordCount(data);
    if (count > 0) return count;
    return data?.id ? 1 : 0;
};

const buildFilterStateSnapshot = (instance, rawData) => {
    const effectiveData = getEffectiveData(instance, rawData);
    const filterState = instance?.filterState || {};

    return {
        selected_ids: Array.isArray(filterState.selectedIds) ? filterState.selectedIds : null,
        source_count: getSnapshotRecordCount(rawData),
        selected_count: getSnapshotRecordCount(effectiveData),
        filter_criteria: filterState.filterCriteria || {},
        updated_at: filterState.updatedAt || null,
    };
};

const buildFilterSummary = (instance, rawData) => {
    const effectiveData = getEffectiveData(instance, rawData);

    return {
        source_count: getSnapshotRecordCount(rawData),
        selected_count: getSnapshotRecordCount(effectiveData),
        source_flight_ids: getSnapshotRecordIds(rawData),
        selected_flight_ids: getSnapshotRecordIds(effectiveData),
    };
};

/**
 * 通用邮件编辑器组件 - V2 双向绑定版本
 * @param {object} props
 * @param {boolean} props.visible 控制Modal显示隐藏
 * @param {string} [props.initialSubject=''] 初始邮件主题
 * @param {string} [props.subjectTemplate=''] 邮件主题模板（支持行内内容块）
 * @param {string} [props.templateContent=''] 包含内容块的模板内容 (HTML)
 * @param {Object} [props.businessContext={}] 完整的业务上下文数据
 * @param {string} [props.to=''] 初始收件人
 * @param {string} [props.cc=''] 初始抄送
 * @param {string} [props.bcc=''] 初始密送
 * @param {Object} [props.templateRecipients] 模板收件人配置
 * @param {function} props.onSubmit 发送回调，参数为 { subject, to, cc, bcc, contentHtml }
 * @param {function} props.onCancel 取消回调
 * @param {string} [props.title='邮件编辑器'] Modal标题
 * @param {Array<string>} [props.toolbarButtons=DEFAULT_TOOLBAR_BUTTONS] 工具栏按钮配置
 * @param {boolean} [props.enableV2Mode=true] 是否启用 V2 双向绑定模式
 * @param {boolean} [props.loading=false] 是否显示loading状态
 * @param {string} [props.loadingText='正在发送...'] loading时显示的文本
 * @param {boolean} [props.cancelDisabled=false] 是否禁用取消按钮
 * @param {Object|null} [props.resendDraftSeed=null] 发件箱重发模式种子（GET /v1/mail/emails/{id}/resend-draft 返回值）。
 *   非空时进入"重发模式"——顶部展示提示条、收件人区显示沿用按钮、发送前做收件人一致性二次确认、附件复用原 OSS 对象。
 *   设计文档：docs/fzx_doc/邮件/发件箱邮件重发/发件箱邮件重发_功能规格说明书.md
 */
const GeneralEmailEditor = forwardRef(
    (
        {
            visible,
            title = "邮件编辑器",
            initialSubject = "",
            subjectTemplate = "", // 新增：邮件主题模板（支持行内内容块）
            to = "",
            cc = "",

            templateRecipients = null, // Template recipients configuration
            templateContent = "", // The raw template from the DB
            businessContext = {}, // The business data context
            onSubmit,
            onCancel,
            showSidePanel = true,
            toolbarButtons = DEFAULT_TOOLBAR_BUTTONS,
            enableV2Mode = true, // V2 模式为默认。设置为 false 可回退到已废弃的 V1 模式。
            // 新增loading相关props
            loading = false, // 是否显示loading状态
            loadingText = "正在发送...", // loading时显示的文本
            cancelDisabled = false, // 是否禁用取消按钮
            // 发件人相关props
            senderOptions = [], // 预设发件账号列表 [{ key, email, display_name, description }]
            defaultSender = null, // 默认发件人对象 { key, email, display_name } 或 null（使用用户绑定邮箱）
            senderEditable = true, // 是否允许切换发件人
            onSenderChange = null, // 发件人选择变化回调
            // 发送选项配置
            emailType = null, // 邮件类型（用于从emailTypeConfig获取配置）
            sendingOptions = {}, // 发送选项配置对象 { [optionType]: value }
            onSendingOptionsChange = null, // 发送选项变化回调 (optionType, value) => void
            // 发件箱邮件重发：种子（T07 消费）
            resendDraftSeed = null,
        },
        ref,
    ) => {
        // ============ 重发模式派生状态 ============
        const isResendMode = resendDraftSeed !== null && resendDraftSeed !== undefined;
        const resendIsBulkEmail = isResendMode && resendDraftSeed?.source_is_bulk_email === true;
        const resendIsFailedSource = isResendMode && resendDraftSeed?.source_status === "Failed";
        // A1 / S8 单航班降级检测：原邮件 business_context_snapshot.schedule 为 {}、flight_ids 为空数组
        const resendDegradedNoBizContext = useMemo(() => {
            if (!isResendMode) return false;
            const snap = resendDraftSeed?.content_block_context?.business_context_snapshot;
            if (!snap || typeof snap !== "object") return true; // 缺失算降级
            const schedule = snap.schedule;
            const scheduleEmpty =
                !schedule || (typeof schedule === "object" && Object.keys(schedule).length === 0);
            const flightIds = snap.flights_summary?.flight_ids || snap.flight_ids;
            const flightsEmpty = !Array.isArray(flightIds) || flightIds.length === 0;
            return scheduleEmpty && flightsEmpty;
        }, [isResendMode, resendDraftSeed]);
        // 失效附件汇总（T14 / FR-008）
        const resendAttachmentsSummary = useMemo(() => {
            if (!isResendMode) return { total: 0, unavailableCount: 0, allUnavailable: false };
            const list = resendDraftSeed?.attachments || [];
            const unavailable = list.filter((a) => a && a.available === false);
            return {
                total: list.length,
                unavailableCount: unavailable.length,
                allUnavailable: list.length > 0 && unavailable.length === list.length,
            };
        }, [isResendMode, resendDraftSeed]);
        const [form] = Form.useForm();
        const [fullscreen, setFullscreen] = useState(false);
        const [attachments, setAttachments] = useState([]);
        const initialRecipientsAppliedRef = useRef(false);

        // 本地发送状态：防止连续点击
        const [localSending, setLocalSending] = useState(false);

        // 发件人状态管理 - 采用React受控组件模式
        // 优先使用外部传入的sender（受控模式），否则使用内部状态（非受控模式）
        const isSenderControlled = onSenderChange !== null;
        const [internalSender, setInternalSender] = useState(defaultSender);
        const selectedSender = isSenderControlled ? defaultSender : internalSender;

        // 同步defaultSender变化到内部状态
        useEffect(() => {
            if (!isSenderControlled) {
                setInternalSender(defaultSender);
            }
        }, [defaultSender, isSenderControlled]);

        // 统一的发件人变更处理
        const handleSenderChange = useCallback(
            (newSender) => {
                if (onSenderChange) {
                    // 受控模式：通知父组件
                    onSenderChange(newSender);
                } else {
                    // 非受控模式：更新内部状态
                    setInternalSender(newSender);
                }
            },
            [onSenderChange],
        );

        // 使用内容块管理Hook
        const { contentBlockInstances, setContentBlockInstances, availableContentBlocks, sidePanelSections, initialContent, createContentBlockInstance, handleEditorUpdate, updateInstanceFilter, processedBusinessContext } = useContentBlocks(templateContent, businessContext, enableV2Mode);

        // 发送选项状态管理 - 采用React受控组件模式
        // 优先使用外部传入的sendingOptions（受控模式），否则使用内部状态（非受控模式）
        const isSendingOptionsControlled = onSendingOptionsChange !== null;

        // 从emailTypeConfig获取初始值
        const initialSendingOptions = emailType ? getInitialOptionValues(emailType) : {};

        const [internalSendingOptions, setInternalSendingOptions] = useState(initialSendingOptions);

        // 发件人状态：使用外部传入的值或内部状态
        const selectedSendingOptions = isSendingOptionsControlled ? sendingOptions : internalSendingOptions;

        // 同步emailType变化到内部状态
        useEffect(() => {
            if (!isSendingOptionsControlled && emailType) {
                const initialValues = getInitialOptionValues(emailType);
                setInternalSendingOptions(initialValues);
            }
        }, [emailType, isSendingOptionsControlled]);

        // 同步外部sendingOptions变化到内部状态（仅用于非受控模式的响应）
        // 注意：sendingOptions 默认参数是 {}，每次渲染都是新对象引用；无条件 setState 会造成
        // "渲染 → effect → setState → 渲染"死循环（受控调用方走不到这里，非受控调用方必踩）。
        // 用函数式更新做浅比较，内容相同返回原引用让 React bail out，循环即被打断。
        useEffect(() => {
            if (!isSendingOptionsControlled && sendingOptions) {
                setInternalSendingOptions((prev) => {
                    const prevObj = prev || {};
                    const keys = Object.keys(sendingOptions);
                    const same = keys.length === Object.keys(prevObj).length && keys.every((k) => prevObj[k] === sendingOptions[k]);
                    return same ? prev : sendingOptions;
                });
            }
        }, [sendingOptions, isSendingOptionsControlled]);

        // 统一的发送选项变更处理
        const handleSendingOptionChange = useCallback(
            (optionType, value) => {
                const newOptions = { ...selectedSendingOptions, [optionType]: value };

                if (onSendingOptionsChange) {
                    // 受控模式：通知父组件
                    onSendingOptionsChange(newOptions);
                } else {
                    // 非受控模式：更新内部状态
                    setInternalSendingOptions(newOptions);
                }
            },
            [selectedSendingOptions, onSendingOptionsChange],
        );

        // 获取所有发送选项（带当前值）
        const visibleOptions = useMemo(() => {
            if (!emailType) return [];
            const options = getAllOptions(emailType);
            // 将当前值注入到选项配置中
            return options.map((option) => ({
                ...option,
                value: selectedSendingOptions[option.type] ?? option.default,
            }));
        }, [emailType, selectedSendingOptions]);

        // 附件管理函数
        const handleAttachmentsChange = useCallback((newAttachments) => {
            setAttachments(newAttachments);
        }, []);

        // 处理内容块生成的附件
        const handleAttachmentGenerated = useCallback((attachment) => {
            setAttachments((prev) => [...prev, attachment]);
        }, []);

        // 处理全局文件拖拽
        const handleGlobalFileDrop = useCallback(async (files) => {
            for (const file of files) {
                try {
                    message.loading(`正在上传 "${file.name}"...`, 0);

                    const result = await uploadFile(file, (progress) => {
                        // 可以在这里显示进度，但为了简化，我们使用loading消息
                    });

                    message.destroy();
                    message.success(`文件 "${file.name}" 上传成功`);

                    const attachmentData = {
                        ...result,
                        file_size: file.size,
                        content_type: file.type,
                    };

                    setAttachments((prev) => [...prev, attachmentData]);
                } catch (error) {
                    message.destroy();
                    console.error("上传失败:", error);
                    message.error(`文件 "${file.name}" 上传失败: ${error.message}`);
                }
            }
        }, []);

        // 使用邮件编辑器Hook
        const { editor, insertContentBlock, insertInlineContentBlock, removeContentBlock, updateContentBlock } = useEmailEditor(processedBusinessContext, initialContent, enableV2Mode, availableContentBlocks, handleEditorUpdate, contentBlockInstances, setContentBlockInstances, createContentBlockInstance, handleAttachmentGenerated, updateInstanceFilter);

        // 高亮相关实例
        const handleInstanceHover = useCallback(
            (instanceId, isHovering) => {
                // 同时高亮编辑器中对应的节点
                if (editor) {
                    const nodeElements = editor.view.dom.querySelectorAll(`[data-instance-id="${instanceId}"]`);
                    nodeElements.forEach((element) => {
                        if (isHovering) {
                            element.classList.add("highlighted");
                        } else {
                            element.classList.remove("highlighted");
                        }
                    });
                }
            },
            [editor],
        );

        // 当编辑器打开时，设置表单初始值和编辑器内容
        useEffect(() => {
            if (visible && editor) {
                // 重置图片体积追踪器（新会话开始）
                resetImageSizeTracker();

                const currentValues = form.getFieldsValue();
                const updates = {};

                // 设置初始收件人（只在首次打开时）
                // 合并多个来源的收件人：模板默认收件人 + 业务来源收件人（并集去重）
                // 发件箱邮件重发：FR-003 重发模式下 to/cc 默认为空，用户必须显式点"沿用原 TO/CC"按钮或自己输入
                if (!initialRecipientsAppliedRef.current) {
                    if (isResendMode) {
                        // 重发：TO/CC 留空，subject 走下方 resendDraftSeed.subject 预填，body 由 templateContent → initialContent 填充
                        updates.to = [];
                        updates.cc = [];
                        console.log("[GeneralEmailEditor] 重发模式：收件人默认为空（FR-003）");
                    } else {
                        // 来源1：to/cc prop（业务数据来源，如合同/订单）
                        const businessToArray = Array.isArray(to) ? to : to ? [to] : [];
                        const businessCcArray = Array.isArray(cc) ? cc : cc ? [cc] : [];

                        // 来源2：templateRecipients（模板默认收件人）
                        let templateToArray = [];
                        let templateCcArray = [];
                        if (templateRecipients) {
                            // 支持两种格式：
                            // 1. { default_to: [...], default_cc: [...] } - 直接数组
                            // 2. { default_to: { addresses: [], groups: [] }, ... } - 原始JSON对象
                            const defaultTo = templateRecipients.default_to;
                            const defaultCc = templateRecipients.default_cc;

                            if (Array.isArray(defaultTo)) {
                                templateToArray = defaultTo;
                            } else if (defaultTo && typeof defaultTo === "object") {
                                // 原始JSON对象格式：合并 addresses 和 groups
                                templateToArray = [...(defaultTo.addresses || []), ...(defaultTo.groups || [])];
                            }

                            if (Array.isArray(defaultCc)) {
                                templateCcArray = defaultCc;
                            } else if (defaultCc && typeof defaultCc === "object") {
                                templateCcArray = [...(defaultCc.addresses || []), ...(defaultCc.groups || [])];
                            }
                        }

                        // 合并并去重
                        const mergedTo = [...new Set([...templateToArray, ...businessToArray])];
                        const mergedCc = [...new Set([...templateCcArray, ...businessCcArray])];

                        if (mergedTo.length > 0 || mergedCc.length > 0) {
                            updates.to = mergedTo;
                            updates.cc = mergedCc;
                            console.log("[GeneralEmailEditor] 初始化收件人:", {
                                templateTo: templateToArray,
                                businessTo: businessToArray,
                                mergedTo,
                                templateCc: templateCcArray,
                                businessCc: businessCcArray,
                                mergedCc,
                            });
                        }
                    }
                    initialRecipientsAppliedRef.current = true;
                }

                // 处理邮件主题
                // 重发模式：subject 直接来自种子（不走 subjectTemplate 占位符解析路径——种子里 subject 是原邮件已渲染的纯文本）
                if (!currentValues.subject) {
                    let resolvedSubject = "";
                    if (isResendMode && resendDraftSeed?.subject) {
                        resolvedSubject = resendDraftSeed.subject;
                    } else if (subjectTemplate && hasValidContentBlocks(subjectTemplate) && processedBusinessContext) {
                        try {
                            resolvedSubject = parseSubjectTemplate(subjectTemplate, processedBusinessContext);
                        } catch (error) {
                            console.error("[GeneralEmailEditor] 主题模板解析失败:", error);
                            const tempDiv = document.createElement("div");
                            tempDiv.innerHTML = subjectTemplate;
                            resolvedSubject = tempDiv.textContent || tempDiv.innerText || subjectTemplate;
                        }
                    } else if (subjectTemplate) {
                        resolvedSubject = subjectTemplate;
                    } else if (initialSubject) {
                        resolvedSubject = initialSubject;
                    }
                    if (resolvedSubject) {
                        updates.subject = resolvedSubject;
                    }
                }

                if (Object.keys(updates).length > 0) {
                    form.setFieldsValue(updates);
                }

                // 设置编辑器内容
                if (editor.isReady && initialContent !== editor.getHTML()) {
                    editor.commands.setContent(initialContent, false);
                }

                // 更新业务上下文
                if (editor.storage) {
                    editor.storage.businessContext = processedBusinessContext;
                }
            }
        }, [visible, initialSubject, subjectTemplate, to, cc, templateRecipients, initialContent, processedBusinessContext, editor, form, isResendMode, resendDraftSeed]);

        // ====== 重发模式：初始化附件 + 强制群发单显默认关闭（T07.d + T10 / FR-007 + T14 / FR-008） ======
        // 这是独立的 useEffect，只在 resendDraftSeed 变化时跑一次，避免污染主初始化逻辑
        const resendInitAppliedRef = useRef(false);
        useEffect(() => {
            if (!visible) {
                resendInitAppliedRef.current = false;
                return;
            }
            if (!isResendMode || resendInitAppliedRef.current) return;
            resendInitAppliedRef.current = true;

            // FR-008：只把 available=true 的附件放进发送列表，available=false 的不进入新邮件
            // 每条附件保留 source_attachment_id + storage_path（让后端 raw_email_service.create_email_attachments 按已上传附件直接复用）
            const usableSeedAttachments = (resendDraftSeed?.attachments || [])
                .filter((a) => a && a.available !== false && a.storage_path)
                .map((a) => ({
                    filename: a.filename,
                    file_size: a.file_size,
                    content_type: a.content_type,
                    storage_path: a.storage_path,
                    object_key: a.storage_path, // 兼容现有 AttachmentList 的 key 字段（按 object_key 做去重 / 删除）
                    source_attachment_id: a.source_attachment_id,
                    // 标记为复用附件，UI 上可显示一个"复用自原邮件"标签（暂存供未来扩展）
                    is_reused_from_resend: true,
                }));
            setAttachments(usableSeedAttachments);

            // T14 提示：失效附件汇总；只在确实有失效项时弹一次 message.warning
            if (resendAttachmentsSummary.unavailableCount > 0) {
                console.warn(
                    `[GeneralEmailEditor] 重发模式：原邮件 ${resendAttachmentsSummary.total} 个附件中 ${resendAttachmentsSummary.unavailableCount} 个 OSS 已不可访问，自动从重发附件列表移除`,
                );
            }

            // FR-007（2026-06-03 修订）：重发时"群发单显"开关【继承原邮件设置】，不再强制关闭。
            //   原因：原邮件 is_bulk_email=true 表示当初是「每个收件人单独发、互不暴露地址」。
            //   旧逻辑把它强制关掉，一旦业务同事补发多个不同收件人，就会被塞进同一封 TO 互相可见 → 泄密。
            //   现在改为：原邮件群发单显(true)→重发默认也开启；原邮件普通单发(false)→维持默认关闭。
            //   只改 BCC_SENDING 这一项，不覆盖整个 sendingOptions；用户仍可在编辑器里手动切换。
            if (resendIsBulkEmail && SendingOptionType?.BCC_SENDING) {
                handleSendingOptionChange(SendingOptionType.BCC_SENDING, true);
                console.log("[GeneralEmailEditor] 重发模式 + 原邮件是群发：继承并默认开启群发单显（FR-007 修订）");
            }
        }, [visible, isResendMode, resendDraftSeed, resendIsBulkEmail, resendAttachmentsSummary]);

        // Handler to toggle fullscreen mode
        const toggleFullscreen = useCallback(() => {
            setFullscreen((prev) => !prev);
        }, []);

        // 合并外部 loading 和内部 localSending 状态
        const isInSendingProcess = loading || localSending;

        // ====== 重发模式 S1.4 二次确认（FR-003 配套 / T07.c） ======
        // 触发条件：所有满足
        //   1. isResendMode === true
        //   2. source_status === 'Sent'（失败邮件按 T12 / S5.2 豁免、未送达再发不算"重复发")
        //   3. source_is_bulk_email === false（群发按 C2 / FR-014 不参与一致性比对）
        //   4. 当前 TO/CC 集合 == 种子 source_to/cc 拆分后的集合（去大小写、去重）
        // 在通过校验后、调 onSubmit 之前弹 Modal.confirm，用户确认才继续
        const shouldConfirmExactlySameRecipients = useCallback(
            (currentTo, currentCc) => {
                if (!isResendMode) return false;
                if (resendDraftSeed?.source_status !== "Sent") return false; // S5.2 失败邮件豁免
                if (resendDraftSeed?.source_is_bulk_email === true) return false; // C2 / FR-014 群发豁免
                const originalTo = splitRecipientString(resendDraftSeed?.source_to_address);
                const originalCc = splitRecipientString(resendDraftSeed?.source_cc_address);
                // TO 必须有且完全一致；CC 如果原邮件有 CC 也要一致；只有 TO 完全一致才认为是"重复发同一封"
                if (originalTo.length === 0) return false;
                if (!recipientsExactlyEqual(currentTo, originalTo)) return false;
                if (originalCc.length > 0 && !recipientsExactlyEqual(currentCc, originalCc)) return false;
                return true;
            },
            [isResendMode, resendDraftSeed],
        );

        const handleOk = async () => {
            // 防止连续点击：如果正在发送中，直接返回
            if (isInSendingProcess) {
                return;
            }

            try {
                const values = await form.validateFields();

                // ====== 重发模式 S1.4 二次确认 ======
                if (shouldConfirmExactlySameRecipients(values.to || [], values.cc || [])) {
                    const userConfirmed = await new Promise((resolve) => {
                        Modal.confirm({
                            title: "收件人与原邮件完全一致",
                            content: "本次重发的收件人与原邮件完全相同，确认要再发一次吗？",
                            okText: "确认重发",
                            cancelText: "返回修改",
                            onOk: () => resolve(true),
                            onCancel: () => resolve(false),
                        });
                    });
                    if (!userConfirmed) {
                        // 用户点了"返回修改"，停在编辑器，不进入发送流程
                        return;
                    }
                }

                // 立即设置本地发送状态，锁定按钮
                setLocalSending(true);

                const rawContentHtml = editor.getHTML();
                // 先将内容块渲染为实际HTML内容，然后清理编辑器属性
                const renderedHtml = renderContentBlocksToHtml(rawContentHtml, processedBusinessContext, contentBlockInstances);
                const contentHtml = cleanHtmlForEmail(renderedHtml);

                // 收集内容块实例数据用于后端上下文感知
                // 从业务上下文中推断模板代码，避免硬编码
                const templateCodeFromContext = processedBusinessContext?.contentBlocks?.find?.((b) => b?.transaction_info?.template_code)?.transaction_info?.template_code || null;

                const contentBlockContext = {
                    template_code: templateCodeFromContext, // 使用上下文中的模板code
                    instances: contentBlockInstances.map((instance) => {
                        // 从processedBusinessContext.contentBlocks中查找对应的business_source信息
                        const businessBlock = processedBusinessContext.contentBlocks?.find((block) => block.id === instance.blockId);
                        const rawData = businessBlock?.data ?? instance.data;

                        return {
                            instanceId: instance.instanceId,
                            blockId: instance.blockId,
                            type: instance.type,
                            title: instance.title,
                            filter_state: buildFilterStateSnapshot(instance, rawData),
                            business_source: businessBlock?.business_source ||
                                instance.business_source || {
                                    entity_type: "Unknown",
                                    entity_ids: [],
                                },
                        };
                    }),
                    // 优化：只保存核心业务数据的快照，避免冗余
                    business_context_snapshot: {
                        // 保存计划基本信息
                        schedule: processedBusinessContext.schedule || {},
                        // 保存航班核心数据（不包含完整的对比结果）
                        flights_summary: {
                            total_count: processedBusinessContext.flights?.length || 0,
                            flight_ids: processedBusinessContext.flights?.map((f) => f.id).filter(Boolean) || [],
                        },
                        // 保存每个内容块的核心数据引用
                        content_blocks_data:
                            contentBlockInstances.map((instance) => {
                                const block = processedBusinessContext.contentBlocks?.find((contentBlock) => contentBlock.id === instance.blockId);
                                const rawData = block?.data ?? instance.data;
                                const effectiveData = getEffectiveData(instance, rawData);

                                return {
                                    id: instance.blockId,
                                    instance_id: instance.instanceId,
                                    type: instance.type,
                                    title: instance.title,
                                    business_source: block?.business_source,
                                    // 只保存数据的关键标识，不保存完整数据
                                    data_summary: {
                                        record_count: getSnapshotRecordCount(effectiveData),
                                        primary_keys: getSnapshotRecordIds(effectiveData),
                                        filter_summary: buildFilterSummary(instance, rawData),
                                    },
                                };
                            }) || [],
                    },
                };

                console.log("[GeneralEmailEditor] 收集的内容块上下文:", contentBlockContext);
                console.log("[GeneralEmailEditor] 原始HTML长度:", rawContentHtml.length);
                console.log("[GeneralEmailEditor] 清理后HTML长度:", contentHtml.length);

                // 提取内嵌图片信息（用于后端 CID 内嵌处理）
                const inlineImages = extractInlineImages(contentHtml);
                if (inlineImages.length > 0) {
                    console.log("[GeneralEmailEditor] 检测到内嵌图片:", inlineImages.length, "张");
                }

                const emailPayload = {
                    ...values,
                    contentHtml,
                    attachments,
                    inlineImages, // 内嵌图片信息，供后端 CID 处理
                    contentBlockContext,
                    sendingOptions: selectedSendingOptions, // 完整的发送选项对象
                    selectedSender, // 选中的发件人（预设账号对象或 null 表示用户绑定邮箱）
                };

                // 使用 Promise.all 确保最少持续 1 秒（防止按钮闪烁）
                // 同时执行 onSubmit 和 1 秒延迟，取较长者
                const minDelayPromise = new Promise((resolve) => setTimeout(resolve, 1000));

                try {
                    // onSubmit 可能是同步或异步函数
                    // 尝试异步执行，如果失败则在 catch 中处理
                    await Promise.all([Promise.resolve(onSubmit(emailPayload)), minDelayPromise]);
                } catch (submitError) {
                    // onSubmit 抛出异常时，记录错误但不重复提示（父组件通常会处理错误提示）
                    console.error("[GeneralEmailEditor] onSubmit 执行失败:", submitError);
                    throw submitError;
                }
            } catch (error) {
                // 表单验证失败或其他错误
                if (error?.errorFields) {
                    console.error("Validation Failed:", error);
                    message.error("请填写所有必填项");
                }
                // 其他错误（如 onSubmit 失败）不在这里提示，由调用方处理
            } finally {
                // 无论成功还是失败，都恢复按钮状态
                setLocalSending(false);
            }
        };

        const handleCancel = () => {
            onCancel();
        };

        useImperativeHandle(ref, () => ({
            // Return public methods that can be called via the ref
            getHTML: () => {
                return editor ? editor.getHTML() : "";
            },
            getJSON: () => {
                return editor ? editor.getJSON() : {};
            },
            setSubject: (text) => {
                form.setFieldsValue({ subject: text });
            },
            getSubject: () => {
                return form.getFieldValue("subject") || "";
            },
            setContent: (content) => {
                if (editor) {
                    editor.commands.setContent(content);
                }
            },
            clearContent: () => {
                if (editor) {
                    editor.commands.clearContent();
                }
            },
            focusEditor: () => {
                if (editor) {
                    editor.commands.focus();
                }
            },
            // V2.5 修改方法
            insertContentBlock,
            insertInlineContentBlock,
            removeContentBlock,
            updateContentBlock,
            createContentBlockInstance,
            getContentBlockInstances: () => contentBlockInstances,
        }));

        return (
            <Modal
                title={null} // 使用自定义标题区域
                open={visible}
                onOk={handleOk}
                onCancel={handleCancel}
                width={fullscreen ? "100%" : 1200}
                footer={null} // 使用自定义底部
                closable={!isInSendingProcess} // 发送过程中禁用关闭按钮
                keyboard={!isInSendingProcess} // 发送过程中禁用ESC键关闭
                styles={{
                    header: { padding: "0" },
                    body: {
                        padding: "0",
                        // 改为overflow: auto确保内容可滚动
                        minHeight: fullscreen ? "calc(100vh - 60px)" : "70vh",
                        maxHeight: fullscreen ? "calc(100vh - 60px)" : "none",
                        overflow: fullscreen ? "auto" : "visible",
                    },
                    // 添加 content 样式，确保它能够适应内容高度
                    content: {
                        height: fullscreen ? "100vh" : "auto", // 全屏模式下使用100vh
                        maxHeight: fullscreen ? "100vh" : "none",
                        display: "flex",
                        flexDirection: "column",
                    },
                    // 全屏模式下占满整个屏幕
                    mask: {
                        background: fullscreen ? "rgba(0, 0, 0, 0.6)" : "rgba(0, 0, 0, 0.45)",
                    },
                    wrapper: {
                        maxWidth: fullscreen ? "100vw" : "none",
                        maxHeight: fullscreen ? "100vh" : "none",
                        top: fullscreen ? 0 : undefined,
                    },
                }}
                className={fullscreen ? "email-editor-modal-fullscreen" : "large-modal"}
                destroyOnHidden={true}
                maskClosable={false}
            >
                <Spin spinning={isInSendingProcess} tip={localSending ? "正在发送..." : loadingText} wrapperClassName="email-editor-spin-wrapper">
                    <GlobalDropZone onFileDrop={handleGlobalFileDrop}>
                        {/* 自定义标题区域 */}
                        <EmailEditorHeader title={title} fullscreen={fullscreen} onToggleFullscreen={toggleFullscreen} />

                        {/* ====== 重发模式顶部 Alert 提示条 ======
                          * T07.a / T14 / T15A 在此合并：
                          *   - 主消息：基于邮件 #N 重发 + 原发送时间
                          *   - A1 / S8 单航班降级：业务上下文为空时多加一行 warning
                          *   - T14 / FR-008 失效附件：有不可用附件时多加一行 warning
                          *   - 群发邮件提示：原邮件是群发单显时一行 info
                          */}
                        {isResendMode && resendDraftSeed && (
                            <Alert
                                type={resendDegradedNoBizContext || resendAttachmentsSummary.unavailableCount > 0 ? "warning" : "info"}
                                showIcon
                                icon={<RetweetOutlined />}
                                style={{ margin: "0 16px 8px 16px" }}
                                message={
                                    <Space size={4}>
                                        <span>
                                            基于邮件 #{resendDraftSeed.source_email_id} 重发
                                        </span>
                                        {resendDraftSeed.source_sent_at && (
                                            <AntdTypography.Text type="secondary" style={{ fontSize: 12 }}>
                                                （原发送时间：{new Date(resendDraftSeed.source_sent_at).toLocaleString("zh-CN", { hour12: false })}）
                                            </AntdTypography.Text>
                                        )}
                                    </Space>
                                }
                                description={
                                    <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                                        {/* A1 / S8 单航班 / 运营邮件降级（FR-012） */}
                                        {resendDegradedNoBizContext && (
                                            <div style={{ color: "#d46b08" }}>
                                                {resendDraftSeed?.content_block_context ? (
                                                    <>原邮件是单航班 / 运营邮件，未保存完整航班快照，业务上下文已置空。若需要恢复业务内容，请关闭重发回到航班监控页重新发起。</>
                                                ) : (
                                                    <>原邮件无业务上下文可继承，本次重发只携带主题、正文和附件。</>
                                                )}
                                            </div>
                                        )}
                                        {/* T14 / FR-008 失效附件汇总 */}
                                        {resendAttachmentsSummary.unavailableCount > 0 && (
                                            <div style={{ color: "#d46b08" }}>
                                                {resendAttachmentsSummary.allUnavailable
                                                    ? `原邮件的全部 ${resendAttachmentsSummary.total} 个附件均已不可用，本次重发不携带任何附件。`
                                                    : `原邮件的 ${resendAttachmentsSummary.unavailableCount} 个附件已不可用，已自动从列表中移除。`}
                                            </div>
                                        )}
                                        {/* C2 / FR-014 群发邮件提示（FR-007 2026-06-03 修订：默认继承群发单显） */}
                                        {resendIsBulkEmail && (
                                            <div style={{ color: "#1890ff" }}>
                                                原邮件是群发单显模式（每个收件人单独发送、互不可见）；本次重发已默认保持"群发单显"开启，你在下方填写的多个收件人会各自单独收到、互相看不到。如确需让收件人互相可见，可手动关闭该开关。
                                            </div>
                                        )}
                                        {/* FR-013 / B2 不可能到这里（按钮就被置灰），但万一种子带 template_available=false 时给一个提醒 */}
                                        {resendDraftSeed.template_available === false && resendDraftSeed.template_code && (
                                            <div style={{ color: "#cf1322" }}>
                                                原模板（{resendDraftSeed.template_code}）已下线，业务上下文可能不完整，请仔细检查后再发送。
                                            </div>
                                        )}
                                    </div>
                                }
                                closable
                            />
                        )}

                        <Form form={form} layout="vertical" className="email-form">
                            <div className="email-content-area">
                                <EmailEditorForm
                                    form={form}
                                    templateRecipients={templateRecipients}
                                    attachments={attachments}
                                    onAttachmentsChange={handleAttachmentsChange}
                                    // 发件人相关 props
                                    senderOptions={senderOptions}
                                    selectedSender={selectedSender}
                                    senderEditable={senderEditable}
                                    onSenderChange={handleSenderChange}
                                    // 发件箱邮件重发：种子透传（用于"沿用原 TO/CC"按钮）
                                    resendDraftSeed={isResendMode ? resendDraftSeed : null}
                                />

                                <EmailEditorContent
                                    editor={editor}
                                    toolbarButtons={toolbarButtons}
                                    showSidePanel={showSidePanel}
                                    enableV2Mode={enableV2Mode}
                                    sidePanelSections={sidePanelSections}
                                    contentBlockInstances={contentBlockInstances}
                                    onInstanceHover={handleInstanceHover}
                                    onInsertBlock={insertContentBlock}
                                    onInsertInlineBlock={insertInlineContentBlock}
                                    onRemoveBlock={removeContentBlock}
                                    onUpdateBlock={updateContentBlock}
                                    // 预览相关 props - 使用响应式数据
                                    form={form}
                                    attachments={attachments}
                                    businessContext={processedBusinessContext}
                                />
                            </div>

                            <EmailEditorFooter onCancel={handleCancel} onOk={handleOk} loading={isInSendingProcess} loadingText={localSending ? "正在发送..." : loadingText} cancelDisabled={cancelDisabled || localSending} visibleOptions={visibleOptions} onOptionChange={handleSendingOptionChange} />
                        </Form>
                    </GlobalDropZone>
                </Spin>
            </Modal>
        );
    },
);

export default GeneralEmailEditor;
